import React, { useState } from 'react';
import { FormMetadata } from '../../types';
import { db } from '@/src/services/db';
import { useAuth } from '@/src/context/AuthContext';
import { Plus, Edit, Copy, Eye, MoreHorizontal, QrCode, X, Archive, Settings } from 'lucide-react';

interface FormManagerProps {
  forms: FormMetadata[];
  onCreate: () => void;
  onEdit: (id: string) => void;
  onViewResponses: (id: string) => void;
}

const FormManager: React.FC<FormManagerProps> = ({ forms, onCreate, onEdit, onViewResponses }) => {
  const { user } = useAuth();
  const [showQrModal, setShowQrModal] = useState<string | null>(null);
  const [activeMenuId, setActiveMenuId] = useState<string | null>(null);

  // --- Actions ---
  const handleDuplicate = async (id: string) => {
    if (!user) return;
    if (confirm('Deseja duplicar este formulário?')) {
        await db.duplicateForm(id, user);
        // Force refresh via parent update would be ideal, but for now we rely on the parent causing a re-render or db update flow
        // In a real app, we'd call a refresh callback. Assuming parent handles prop updates.
        window.location.reload(); // Simple refresh to show new form since we don't have a refresh prop
    }
  };

  const handleArchive = (id: string) => {
      alert(`Formulário ${id} arquivado (Simulação).`);
      setActiveMenuId(null);
  };

  return (
    <div className="space-y-6 animate-in fade-in duration-300">
      <div className="flex justify-between items-end">
        <div>
            <h2 className="text-2xl font-bold text-slate-800">Meus Formulários</h2>
            <p className="text-sm text-slate-500">Gerencie definições, versões e distribuição.</p>
        </div>
        <button 
            onClick={onCreate}
            className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-md shadow-sm flex items-center gap-2 text-sm font-medium transition-colors"
        >
            <Plus size={16} /> Novo Formulário
        </button>
      </div>

      <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden min-h-[400px]">
        <table className="w-full text-left border-collapse">
            <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                    <th className="px-4 py-3 text-xs font-bold text-slate-500 uppercase tracking-wider w-8">Status</th>
                    <th className="px-4 py-3 text-xs font-bold text-slate-500 uppercase tracking-wider">Título</th>
                    <th className="px-4 py-3 text-xs font-bold text-slate-500 uppercase tracking-wider">Setor</th>
                    <th className="px-4 py-3 text-xs font-bold text-slate-500 uppercase tracking-wider">Versão Atual</th>
                    <th className="px-4 py-3 text-xs font-bold text-slate-500 uppercase tracking-wider">Atualizado em</th>
                    <th className="px-4 py-3 text-xs font-bold text-slate-500 uppercase tracking-wider text-right">Ações</th>
                </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
                {forms.map(form => (
                    <tr key={form.id} className="group hover:bg-slate-50 transition-colors relative">
                        <td className="px-4 py-3">
                            <span className={`inline-block w-2.5 h-2.5 rounded-full ${form.status === 'active' ? 'bg-green-500' : 'bg-slate-300'}`}></span>
                        </td>
                        <td className="px-4 py-3">
                            <div className="font-semibold text-sm text-slate-800">{form.title}</div>
                            <div className="text-xs text-slate-400 truncate max-w-[200px]">{form.description}</div>
                        </td>
                        <td className="px-4 py-3 text-xs font-medium text-slate-600 bg-slate-50 rounded-sm">
                            <span className="bg-slate-100 px-2 py-1 rounded">{form.sector}</span>
                        </td>
                        <td className="px-4 py-3">
                            <span className="text-xs font-mono bg-indigo-50 text-indigo-700 px-1.5 py-0.5 rounded border border-indigo-100">
                                v{form.currentVersion}.0
                            </span>
                        </td>
                        <td className="px-4 py-3 text-xs text-slate-500">
                            {new Date(form.updatedAt).toLocaleDateString()}
                        </td>
                        <td className="px-4 py-3 text-right">
                            <div className="flex items-center justify-end gap-1 opacity-100 sm:opacity-0 group-hover:opacity-100 transition-opacity">
                                <button onClick={() => onEdit(form.id)} className="p-1.5 text-slate-500 hover:text-indigo-600 hover:bg-indigo-50 rounded" title="Editar">
                                    <Edit size={16} />
                                </button>
                                <button onClick={() => handleDuplicate(form.id)} className="p-1.5 text-slate-500 hover:text-slate-800 hover:bg-slate-100 rounded" title="Duplicar">
                                    <Copy size={16} />
                                </button>
                                <button onClick={() => onViewResponses(form.id)} className="p-1.5 text-slate-500 hover:text-slate-800 hover:bg-slate-100 rounded" title="Ver Respostas">
                                    <Eye size={16} />
                                </button>
                                <button onClick={() => setShowQrModal(form.id)} className="p-1.5 text-slate-500 hover:text-slate-800 hover:bg-slate-100 rounded" title="Gerar QR Code">
                                    <QrCode size={16} />
                                </button>
                                <div className="relative">
                                    <button 
                                        onClick={() => setActiveMenuId(activeMenuId === form.id ? null : form.id)}
                                        className="p-1.5 text-slate-500 hover:text-slate-800 hover:bg-slate-100 rounded"
                                    >
                                        <MoreHorizontal size={16} />
                                    </button>
                                    {activeMenuId === form.id && (
                                        <div className="absolute right-0 top-8 w-40 bg-white shadow-xl rounded-lg border border-slate-100 z-50 overflow-hidden animate-in fade-in zoom-in-95 duration-200">
                                            <button onClick={() => handleArchive(form.id)} className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2">
                                                <Archive size={14}/> Arquivar
                                            </button>
                                            <button className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2">
                                                <Settings size={14}/> Configurações
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </td>
                    </tr>
                ))}
            </tbody>
        </table>
        {forms.length === 0 && (
            <div className="p-10 text-center text-slate-400 text-sm">Nenhum formulário criado ainda. Clique em "Novo Formulário" para começar.</div>
        )}
      </div>

      {/* --- QR CODE MODAL --- */}
      {showQrModal && (
        <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center z-50 p-4" onClick={() => setShowQrModal(null)}>
            <div className="bg-white rounded-xl shadow-2xl p-6 max-w-sm w-full text-center" onClick={e => e.stopPropagation()}>
                <div className="flex justify-between items-center mb-4">
                    <h3 className="font-bold text-slate-800">Acesso Rápido</h3>
                    <button onClick={() => setShowQrModal(null)} className="text-slate-400 hover:text-slate-600"><X size={20}/></button>
                </div>
                <div className="bg-white p-4 border-2 border-slate-900 rounded-xl inline-block mb-4">
                    {/* Mock QR */}
                    <QrCode size={150} className="text-slate-900"/>
                </div>
                <p className="text-sm font-bold text-slate-800 mb-1">
                    {forms.find(f => f.id === showQrModal)?.title}
                </p>
                <p className="text-xs text-slate-500 mb-4">Escaneie com o App do Operador</p>
                <div className="bg-slate-100 p-2 rounded text-xs font-mono text-slate-600 break-all select-all cursor-pointer hover:bg-slate-200">
                    omniform://form/{showQrModal}
                </div>
            </div>
        </div>
      )}

      {/* Close menu when clicking outside */}
      {activeMenuId && (
        <div className="fixed inset-0 z-40" onClick={() => setActiveMenuId(null)}></div>
      )}
    </div>
  );
};

export default FormManager;
